import { useMemo } from 'react'

import { useIntl } from 'react-intl'

import {
  FilterOperator,
  MatchFilterInput,
  QueryFilterPostsArgs,
} from '@tribeplatform/gql-client/types'
import { useFilterPosts } from '@tribeplatform/react-sdk/hooks'

import { useCommandBar } from '../../../CommandBar/CommandBarContext.js'
import {
  POSTS_FIELDS,
  SmartSearchEntityType,
  TRENDING_POSTS_FILTERS,
} from '../../index.js'
import { ResultsView } from '../../SmartSearch.types.js'
import { SearchEntityRow } from '../SmartSearchResults/SearchEntityRow.js'
import { SectionTitle } from '../SmartSearchResults/SectionTitle.js'

type Props = {
  view: ResultsView
}

export const TrendingPosts = ({ view }: Props) => {
  const { $t } = useIntl()
  const { searchOptions } = useCommandBar()

  const spaceFilters: MatchFilterInput = useMemo(
    () =>
      searchOptions?.spaceIds?.length && {
        key: 'spaceId',
        any: searchOptions.spaceIds?.map(spaceId => JSON.stringify(spaceId)),
      },
    [searchOptions?.spaceIds],
  )

  const dateFrom = useMemo(() => {
    const now = new Date()
    // Set time to beginning of the day (00:00:00)
    now.setHours(0, 0, 0, 0)
    return JSON.stringify(
      new Date(
        now.getTime() -
          TRENDING_POSTS_FILTERS.publishedAt.daysFrom * 24 * 60 * 60 * 1000,
      ),
    )
  }, [])

  const variables: QueryFilterPostsArgs = useMemo(
    () => ({
      limit: 10,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              // exclude replies
              {
                key: 'parentPostId',
                isNull: true,
              },
              {
                key: 'publishedAt',
                range: {
                  gte: dateFrom,
                },
              },
              ...(spaceFilters ? [spaceFilters] : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
      orderBy: 'totalRepliesCount',
    }),
    [dateFrom, spaceFilters],
  )

  const { data, isLoading } = useFilterPosts({
    variables,
    fields: POSTS_FIELDS,
  })

  if (isLoading || !data) {
    return null
  }

  return (
    <div className="flex flex-col pb-3 relative">
      <SectionTitle
        type="trending-posts"
        title={$t({
          defaultMessage: 'Trending posts',
          id: 'SmartSearch.TrendingPosts',
        })}
        view={view}
      />

      <div className="px-2">
        {data.nodes.map(post => (
          <SearchEntityRow
            key={post.id}
            item={post}
            type={SmartSearchEntityType.POSTS}
          />
        ))}
      </div>
    </div>
  )
}
